import { CONTRACT_SIGN_STATES, ContractSignState } from './contractsViewsModel';

/**
 * ТЗ 0120, этап 2 — окно договора «всё главное на одном экране»: состояние
 * подписи, срок ссылки, шкала «Отправлен → Кликнул → Открыл → Подписал» и
 * виды ИИ-писем по договору с причиной «почему нет».
 *
 * Состояние подписи и срок ссылки — поля сервера (`signState`,
 * `expiresInDays` карточки). Дата клика по короткой ссылке — из лестницы SMS
 * (`link.firstHumanClickedAt`): это дата, а не состояние.
 */

/** `signState` сервера → известное состояние; незнакомое — «не отправлен». */
export const contractSignStateOf = (
  value: string | null | undefined,
): ContractSignState =>
  (CONTRACT_SIGN_STATES as readonly string[]).includes(value || '')
    ? (value as ContractSignState)
    : 'not_sent';

/** Факты договора для шкалы подписи. */
export interface IContractSignTimelineFacts {
  status: string;
  sentAt?: string | null;
  openedAt?: string | null;
  signedAt?: string | null;
  completedAt?: string | null;
}

/* ── Шкала подписи ─────────────────────────────────────────────────── */

export const CONTRACT_SIGN_STEPS = [
  'sent',
  'clicked',
  'opened',
  'signed',
] as const;
export type ContractSignStep = typeof CONTRACT_SIGN_STEPS[number];

export interface IContractSignStepView {
  key: ContractSignStep;
  /** Шаг пройден (или пройден следующий — тогда без даты). */
  done: boolean;
  /** Когда — если известно. */
  at: string | null;
}

/**
 * Четыре шага. Шаг считается пройденным, если пройден любой следующий:
 * открыл в Documenso, не кликая короткую ссылку (ссылка из письма Documenso
 * или длинная), — «Кликнул» отмечен без даты.
 */
export const contractSignTimeline = (
  doc: IContractSignTimelineFacts,
  clickedAt: string | null,
  /** `signState` сервера: «кликнул» он знает и без даты клика. */
  signState: ContractSignState | null = null,
): IContractSignStepView[] => {
  const signedAt =
    doc.signedAt ||
    (doc.status === 'completed' ? doc.completedAt || null : null);
  const signed = Boolean(signedAt) || doc.status === 'completed';
  const opened = signed || Boolean(doc.openedAt) || doc.status === 'opened';
  const clicked = opened || Boolean(clickedAt) || signState === 'clicked';
  const sent = clicked || Boolean(doc.sentAt);
  return [
    { key: 'sent', done: sent, at: doc.sentAt || null },
    { key: 'clicked', done: clicked, at: clickedAt },
    { key: 'opened', done: opened, at: doc.openedAt || null },
    { key: 'signed', done: signed, at: signedAt },
  ];
};

/* ── ИИ-письма по договору ─────────────────────────────────────────── */

/** Виды ряда «Написать» окна договора (без `invite` — он в окне отправки). */
export const CONTRACT_EMAIL_AI_KINDS = [
  'reminder',
  'expiring',
  'followup',
  'after_signing',
] as const;
export type ContractEmailAiBarKind = typeof CONTRACT_EMAIL_AI_KINDS[number];
export type ContractEmailAiKind = ContractEmailAiBarKind | 'invite';

/** Почему вид сейчас не написать — код причины (`esign.emailAi.blocks.*`). */
export type ContractEmailAiBlock =
  | 'notSent'
  | 'signed'
  | 'closed'
  | 'noExpiry'
  | 'shortlinkOff'
  | 'notSigned';

export interface IContractEmailAiFacts {
  signState: ContractSignState;
  expiresAt?: string | null;
  shortLinksEnabled: boolean;
}

/**
 * Охрана видов — та же, что у сервера (`contract_email_ai.check_kind`):
 * напоминание и «срок истекает» — живая ссылка и сократитель (у «срока» ещё
 * и `expiresAt`), «после подписи» — только подписанный, follow-up — всегда.
 */
export const contractEmailAiBlock = (
  kind: ContractEmailAiBarKind,
  facts: IContractEmailAiFacts,
): ContractEmailAiBlock | null => {
  const { signState } = facts;
  if (kind === 'followup') return null;
  if (kind === 'after_signing') {
    return signState === 'signed' ? null : 'notSigned';
  }
  if (signState === 'not_sent') return 'notSent';
  if (signState === 'signed') return 'signed';
  if (signState === 'closed') return 'closed';
  if (kind === 'expiring' && !facts.expiresAt) return 'noExpiry';
  if (!facts.shortLinksEnabled) return 'shortlinkOff';
  return null;
};

/** Коды отказа `draftContractEmail` → `esign.emailAi.errors.<code>`. */
export const CONTRACT_EMAIL_AI_ERRORS = [
  'ai_disabled',
  'budget_exceeded',
  'bad_input',
  'in_progress',
  'internal_error',
  'FORBIDDEN',
  'NOT_YOUR_CONTRACT',
  'WRONG_STATUS',
  'LINK_NOT_READY',
  'SHORTLINK_DISABLED',
  'NO_EXPIRY',
  'NOT_SIGNED',
  'NETWORK',
] as const;

export const contractEmailAiErrorKey = (code: string | null | undefined) =>
  code && (CONTRACT_EMAIL_AI_ERRORS as readonly string[]).includes(code)
    ? `esign.emailAi.errors.${code}`
    : null;
